import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AppShell from '../components/AppShell.tsx';
import Button from '../components/Button.tsx';
import EvidencePanel from '../components/EvidencePanel.tsx';
import { Field, Select, TextArea, TextInput } from '../components/Field.tsx';
import Metric from '../components/Metric.tsx';
import { ExperimentStatusWord, ThresholdWord } from '../components/StatusWord.tsx';
import { apiClient, type ExperimentSummary } from '../lib/api.ts';
import { daysElapsedMetric } from '../lib/daysElapsedMetric.ts';
import {
  MANUAL_EVENT_KINDS,
  conversionLabel,
  type EventKind,
} from '../../netlify/functions/_lib/domain.ts';

const MANUAL_KINDS = MANUAL_EVENT_KINDS;

function siteOrigin(): string {
  return (import.meta.env.VITE_SITE_URL as string | undefined) || window.location.origin;
}

export default function ExperimentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<ExperimentSummary | null>(null);
  const [error, setError] = useState('');
  const [eventKind, setEventKind] = useState<EventKind>('waitlist');
  const [quantity, setQuantity] = useState(1);
  const [eventNote, setEventNote] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [formError, setFormError] = useState('');

  const reload = useCallback(async () => {
    if (!id) return;
    const next = await apiClient.summary(id);
    setData(next);
  }, [id]);

  useEffect(() => {
    void reload().catch((err: unknown) => {
      setError(err instanceof Error ? err.message : 'Not found');
    });
  }, [reload]);

  async function addEvent(event: FormEvent) {
    event.preventDefault();
    if (!id) return;
    setFormError('');
    try {
      await apiClient.addEvent(id, { kind: eventKind, quantity, note: eventNote });
      setEventNote('');
      await reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not record that event.');
    }
  }

  async function recordWaitlistJoin() {
    if (!id) return;
    setFormError('');
    try {
      await apiClient.addEvent(id, { kind: 'waitlist', quantity: 1 });
      await reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not record that waitlist join.');
    }
  }

  async function addNote(event: FormEvent) {
    event.preventDefault();
    if (!id) return;
    setFormError('');
    try {
      await apiClient.addNote(id, noteBody);
      setNoteBody('');
      await reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not save that note.');
    }
  }

  async function addLink(event: FormEvent) {
    event.preventDefault();
    if (!id) return;
    setFormError('');
    try {
      await apiClient.addLink(id, { destination_url: linkUrl, label: linkLabel });
      setLinkUrl('');
      setLinkLabel('');
      await reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not create that link.');
    }
  }

  async function removeExperiment() {
    if (!id) return;
    if (!window.confirm('Delete this experiment and everything attached to it?')) return;
    await apiClient.deleteExperiment(id);
    navigate('/app');
  }

  if (error) {
    return (
      <AppShell>
        <main className="mx-auto max-w-prose px-6 py-10">
          <h1 className="text-3xl">That experiment is not here.</h1>
          <p className="mt-3 text-ink-muted">{error}</p>
          <p className="mt-6">
            <Link to="/app" className="text-accent-600 underline underline-offset-4">
              Back to experiments
            </Link>
          </p>
        </main>
      </AppShell>
    );
  }

  if (!data) {
    return (
      <AppShell>
        <main className="mx-auto max-w-5xl px-6 py-10">
          <p className="text-ink-muted">Loading experiment…</p>
        </main>
      </AppShell>
    );
  }

  const { experiment, funnel, recommendation, events, notes, links, decisions } = data;
  const label = conversionLabel(experiment.success_event_kind, experiment.success_label);
  const days = daysElapsedMetric({
    status: experiment.status,
    daysElapsed: funnel.daysElapsed,
    conversionLabel: label,
    conversionRate: funnel.conversionRate,
  });

  return (
    <AppShell>
      <main className="mx-auto max-w-5xl px-6 py-10">
        <p className="text-sm">
          <Link to="/app" className="text-accent-600 underline underline-offset-4">
            All experiments
          </Link>
        </p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl">{experiment.title}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <ExperimentStatusWord status={experiment.status} />
              <ThresholdWord state={recommendation.thresholdState} />
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to={`/app/experiments/${experiment.id}/edit`}
              className="inline-flex rounded border border-border bg-surface px-4 py-2 text-sm"
            >
              Edit
            </Link>
            <Button tone="danger" onClick={() => void removeExperiment()}>
              Delete
            </Button>
          </div>
        </div>

        <section className="mt-8 grid gap-4 sm:grid-cols-3">
          <article className="rounded-xl border border-border bg-surface p-5 shadow-card sm:col-span-3">
            <h2 className="text-lg">The bet</h2>
            <dl className="mt-3 grid gap-4 sm:grid-cols-3">
              <div>
                <dt className="text-sm text-ink-subtle">Audience</dt>
                <dd className="mt-1">{experiment.audience}</dd>
              </div>
              <div>
                <dt className="text-sm text-ink-subtle">Message</dt>
                <dd className="mt-1">{experiment.message}</dd>
              </div>
              <div>
                <dt className="text-sm text-ink-subtle">Offer</dt>
                <dd className="mt-1">{experiment.offer}</dd>
              </div>
            </dl>
            {experiment.channel ? (
              <p className="mt-4 text-sm text-ink-muted">Channel: {experiment.channel}</p>
            ) : null}
          </article>
        </section>

        <section className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Metric
            label={label}
            value={`${funnel.conversions} / ${experiment.success_threshold}`}
            hint="Count toward the threshold. Not a rate gate."
          />
          <Metric label="Clicks" value={funnel.clicks} hint="Tracked link clicks, counted once per visitor." />
          <Metric
            label="Impressions + likes"
            value={funnel.impressions + funnel.likes}
            hint="Visible, never treated as demand."
          />
          <Metric label="Days elapsed" value={days.value} hint={days.hint} />
        </section>

        <section className="mt-6 rounded-xl border border-border bg-surface p-6 shadow-card">
          <h2 className="text-xl">Waitlist yes</h2>
          <p className="mt-1 text-sm text-ink-muted">
            Someone joined. Put it on the record. Other kinds stay in the form below.
          </p>
          <Button className="mt-4" onClick={() => void recordWaitlistJoin()}>
            Record a waitlist join
          </Button>
        </section>

        <div className="mt-8">
          <EvidencePanel
            key={`${recommendation.ruleId}-${funnel.totalEvents}-${funnel.conversions}`}
            recommendation={recommendation}
            onConfirm={async (payload) => {
              await apiClient.saveDecision(experiment.id, payload);
              await reload();
            }}
          />
        </div>

        {decisions.length > 0 ? (
          <section className="mt-8">
            <h2 className="text-xl">Saved decisions</h2>
            <ul className="mt-3 space-y-3">
              {decisions.map((decision) => (
                <li key={decision.id} className="rounded border border-border bg-surface p-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <ThresholdWord state={decision.threshold_state} />
                    <span className="text-sm text-ink-subtle">
                      {new Date(decision.created_at).toLocaleString()}
                      {decision.edited ? ' · edited before save' : ''}
                    </span>
                  </div>
                  <p className="mt-2">{decision.summary}</p>
                  <p className="mt-2 text-ink-muted">{decision.next_step}</p>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="mt-10 grid gap-8 lg:grid-cols-2">
          <div className="rounded-xl border border-border bg-surface p-6 shadow-card">
            <h2 className="text-xl">Tracked links</h2>
            <p className="mt-1 text-sm text-ink-muted">
              Destination is checked when you save the link and again at redirect time.
            </p>
            <form className="mt-4 space-y-3" onSubmit={(event) => void addLink(event)}>
              <Field label="Destination URL">
                <TextInput
                  type="url"
                  required
                  placeholder="https://example.com/waitlist"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                />
              </Field>
              <Field label="Label">
                <TextInput value={linkLabel} onChange={(e) => setLinkLabel(e.target.value)} />
              </Field>
              <Button type="submit">Create tracked link</Button>
            </form>
            <ul className="mt-4 space-y-2">
              {links.map((link) => (
                <li key={link.id} className="rounded border border-border px-3 py-2 text-sm">
                  <p className="font-mono break-all">
                    {siteOrigin()}/r/{link.slug}
                  </p>
                  <p className="mt-1 text-ink-muted">{link.destination_url}</p>
                  <Button tone="quiet" className="mt-1" onClick={() => void apiClient.deleteLink(link.id).then(reload)}>
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-xl border border-border bg-surface p-6 shadow-card">
            <h2 className="text-xl">Record what happened</h2>
            <form className="mt-4 space-y-3" onSubmit={(event) => void addEvent(event)}>
              <Field label="Kind">
                <Select
                  value={eventKind}
                  onChange={(e) => setEventKind(e.target.value as EventKind)}
                >
                  {MANUAL_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {kind.replace('_', ' ')}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Quantity">
                <TextInput
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={(e) => setQuantity(Number(e.target.value))}
                />
              </Field>
              <Field label="Note">
                <TextInput value={eventNote} onChange={(e) => setEventNote(e.target.value)} />
              </Field>
              <Button type="submit">Add event</Button>
            </form>
            <ul className="mt-4 divide-y divide-border text-sm">
              {events.map((item) => (
                <li key={item.id} className="flex items-start justify-between gap-3 py-2">
                  <span>
                    <span className="font-mono">{item.quantity}</span> {item.kind.replace('_', ' ')}
                    {item.note ? <span className="text-ink-muted"> — {item.note}</span> : null}
                  </span>
                  {item.kind !== 'link_click' && item.source !== 'creem' ? (
                    <Button tone="quiet" onClick={() => void apiClient.deleteEvent(item.id).then(reload)}>
                      Remove
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mt-8 rounded-xl border border-border bg-surface p-6 shadow-card">
          <h2 className="text-xl">Notes</h2>
          <form className="mt-4 space-y-3" onSubmit={(event) => void addNote(event)}>
            <Field label="What you heard or saw">
              <TextArea value={noteBody} onChange={(e) => setNoteBody(e.target.value)} required />
            </Field>
            <Button type="submit">Add note</Button>
          </form>
          <ul className="mt-4 space-y-3">
            {notes.map((note) => (
              <li key={note.id} className="rounded border border-border px-3 py-2">
                <p>{note.body}</p>
                <p className="mt-1 text-sm text-ink-subtle">
                  {new Date(note.created_at).toLocaleString()}
                </p>
                <Button tone="quiet" onClick={() => void apiClient.deleteNote(note.id).then(reload)}>
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        </section>

        {formError ? <p className="mt-6 text-danger">{formError}</p> : null}
      </main>
    </AppShell>
  );
}
